import { COST_COMPONENT_ORDER } from 'src/constants/componentConstants/costModelling'
// import {
//   What_If_Compare_Component_Name_Mapping,
//   What_If_Compare_Component_Order,
// } from 'src/constants/componentConstants/costModelling/whatIfSimulation'
import { v4 as uuidv4 } from 'uuid'

const capitalizeFirstLetter = (str) => {
  return str
    ?.replace(/\b\w/g, function (char) {
      return char.toUpperCase()
    })
    ?.replace('_', ' ')
}

export const TransformComponentData = (data) => {
  const componentKeysMap = {
    'raw material cost': 'raw_materials',
    'packaging cost': 'packaging_cost',
    'labor cost': 'labor_cost',
    'Freight cost': 'freight_cost',
  }

  const validKeys = [
    'overhead cost',
    'raw material cost',
    'processing cost',
    'labor cost',
    'packaging cost',
    'profit margin',
    'Freight cost',
    'tariff rate',
  ]

  const componentNameKeyMap = {
    'raw material cost': 'Raw Material',
    'processing cost': 'Conversion',
    'labor cost': 'Labor',
    'packaging cost': 'Packaging',
    'overhead cost': 'Overhead & Margin',
    'profit margin': 'Overhead & Margin',
    'Freight cost': 'Logistics',
    'tariff rate': 'Duty & Tariff',
  }

  // Track added labels to avoid duplicates
  const addedLabels = new Map()

  // Ensure the necessary keys are defined before processing
  if (!data || !data.break_down || !data.break_down_details) {
    return []
  }

  Object.keys(data.break_down).forEach((key) => {
    if (validKeys.includes(key) && data?.break_down[key] !== 0) {
      const label = componentNameKeyMap[key] || key
      const percentage = parseFloat(data.break_down[key]) || 0
      let detailData = []

      if (Object.prototype.hasOwnProperty.call(componentKeysMap ?? {}, key)) {
        const mappedKey = componentKeysMap[key]
        const detail = data.break_down_details[mappedKey] || {}
        detailData = Object.keys(detail).map((detailKey) => ({
          label: capitalizeFirstLetter(detailKey),
          percentage: Object.prototype.hasOwnProperty.call(detail[detailKey], 'percent')
            ? detail[detailKey].percent
            : typeof detail[detailKey] === 'string'
            ? detail[detailKey].replace('%', '')
            : detail[detailKey],
          indexTicker: Object.prototype.hasOwnProperty.call(detail[detailKey], 'indexTicker')
            ? detail[detailKey].indexTicker === 'NULL' || detail[detailKey].indexTicker === 'null'
              ? ''
              : detail[detailKey].indexTicker
            : '',
          indexId: uuidv4(),
        }))
      }

      // Check if the label has already been added
      if (addedLabels.has(label)) {
        // Update the percentage if the label already exists
        const existingEntry = addedLabels.get(label)
        existingEntry.percentage += percentage
        existingEntry.data.push(...detailData) // Add new details to existing entry
      } else {
        // Add a new entry for the label
        addedLabels.set(label, {
          label,
          percentage,
          data: detailData,
        })
      }
    }
  })

  // // Convert the added labels map to an array and sort based on COST_COMPONENT_ORDER
  // if (!Array.isArray(COST_COMPONENT_ORDER)) {
  //   return [] // Return empty array if COST_COMPONENT_ORDER is not defined
  // }

  // Convert the added labels map to an array and sort based on COST_COMPONENT_ORDER
  const sortedData = Array.from(addedLabels.values())?.sort(
    (a, b) => COST_COMPONENT_ORDER[a.label] - COST_COMPONENT_ORDER[b.label],
  )

  return sortedData
}

export function transformData(data) {
  // Check if the data is in transformed format by checking for the 'subcomponent_name' key
  const isTransformed = data?.some((component) => component.sub_components)

  return data?.map((component, componentIndex) => {
    if (isTransformed) {
      // Reverse transform subcomponents
      const subComponents = component.sub_components.map((subcomponent) => {
        const actualPercentage = Number(Number(subcomponent.subcomponent_percentage).toFixed(2))

        const displayPercentage =
          component.section_id === 4
            ? Number(
                (
                  (Number(component.component_percentage).toFixed(2) * actualPercentage) /
                  100
                ).toFixed(2),
              )
            : undefined

        return {
          label: subcomponent.subcomponent_name,

          percentage: actualPercentage,

          // spread displayPercentage for ohm section only
          ...(component.section_id === 4 && {
            displayPercentage,
          }),

          subcomponentCost: subcomponent?.subcomponent_cost || 0,
          indexTicker: subcomponent.subcomponent_index_ticker,
          indexTickerDescription: subcomponent?.description,
          indexId: uuidv4(),
          simulationComponentBreakdownId: subcomponent?.simulation_component_breakdown_id,
          isTickerMissing: subcomponent?.is_ticker_missing,
          isTickerRequired: subcomponent?.is_ticker_required,
          baseModelSubComponentCost: subcomponent?.base_model_subcomponent_cost,
          baseModelIndexTicker: subcomponent?.base_model_index_ticker,
          baseModelIndexTickerCountry: subcomponent?.base_model_index_ticker_country,
          tickerType: subcomponent.ticker_type,
          gtoTickerJson: subcomponent.gto_ticker_json,
          adderPercentage: subcomponent?.adder ? subcomponent.adder.toString() : '0',
          indexTickerCountry: subcomponent?.index_ticker_country,
          baseModelIndexUom: subcomponent?.base_model_index_ticker_uom,
          baseModelTickerCurrentCost: subcomponent?.base_model_ticker_current_cost,
          baseModelIndexTickerDescription: subcomponent?.base_model_index_ticker_description,
          percentageChange: subcomponent?.subcomponent_percentage_change || 0,
          subComponentDetails: subcomponent?.subcomponent_details,
          baseModelCommodityL1: subcomponent?.base_model_index_ticker_l1,
          baseModelCommodityL2: subcomponent?.base_model_index_ticker_l2,
          baseModelCommodityL3: subcomponent?.base_model_index_ticker_l3,
          baseModelTickerDataSource: subcomponent?.base_model_ticker_data_source,
          adderDescription: subcomponent?.adder_description,
          adderReferenceLinks: subcomponent?.adder_reference_link || [],
        }
      })

      const componentData = {
        label: component.component_name,
        sectionId: component.section_id,

        percentage: Number(component.component_percentage).toFixed(2),

        componentCost: component.component_cost || 0,
        data: subComponents,
        simulationCostBreakdownId: component?.simulation_cost_breakdown_id,
        percentageChange: component.component_percentage_change || 0,
        componentDetails: component?.component_details,
        baseModelComponentCost: component?.base_model_component_cost,
      }

      if (component.cost_driver_id) {
        componentData.costDriverId = component.cost_driver_id
      }

      return componentData
    } else {
      // Transform subcomponents
      const subComponents = component.data.map((subcomponent, subIndex) => ({
        subcomponent_name: subcomponent.label,
        subcomponent_percentage: subcomponent.percentage,
        subcomponent_index_ticker: subcomponent.indexTicker,
        subcomponent_cost: subcomponent?.subcomponentCost,
        subcomponent_order_id: subIndex + 1, // Subcomponent order starts from 1
        description: subcomponent?.indexTickerDescription,
        simulation_component_breakdown_id: subcomponent?.simulationComponentBreakdownId,
        is_ticker_missing: subcomponent?.isTickerMissing,
        adder: subcomponent?.adderPercentage || 0,
        subcomponent_details: subcomponent?.subComponentDetails,
        subcomponent_percentage_change: subcomponent?.percentageChange || 0,
        // index_ticker_country: subcomponent?.indexTickerCountry,
        adder_description: subcomponent?.adderDescription || null,
        adder_reference_link: subcomponent?.adderReferenceLinks || [],
      }))

      // Check for costDriverId and assign it as cost_driver_id if present
      const componentData = {
        component_name: component.label,
        section_id: component.sectionId,
        component_cost: component.componentCost,
        component_percentage: component.percentage,
        sub_components: subComponents,
        order_id: componentIndex + 1, // Component order starts from 1
        simulation_cost_breakdown_id: component?.simulationCostBreakdownId,
        component_details: component?.componentDetails,
        component_percentage_change: component?.percentageChange,
      }

      if (component.costDriverId) {
        componentData.cost_driver_id = component.costDriverId // Assigning cost_driver_id if present
      }

      // Return the transformed component object
      return componentData
    }
  })
}

export function componentsWiseGrouping(data) {
  return Object.values(
    data.reduce((acc, item) => {
      const key = item?.cost_category
      if (!acc[key]) {
        acc[key] = {
          component_name: item?.cost_category,
          component_order_id: item?.component_order_id,
          component_percentage: item?.component_percentage,
          section_id: item?.section_id,
          simulation_cost_breakdown_id: item?.simulation_cost_breakdown_id,
          sub_components: [],
        }
      }
      // if component don't have subcomponents don't push it in data key
      if (!item?.cost_element) {
        return acc
      } else {
        acc[key].sub_components.push({
          simulation_component_breakdown_id: item?.simulation_component_breakdown_id,
          description: item?.description,
          subcomponent_index_ticker: item?.index_ticker,
          subcomponent_name: item?.cost_element,
          subcomponent_order_id: item?.subcomponent_order_id,
          subcomponent_percentage: item?.sub_component_percentage,
        })
      }
      return acc
    }, {}),
  )
}

export function distributeSimulationCost(data) {
  const totalCost = data.simulation_cost
  data?.bill_of_material?.forEach((component) => {
    const componentCost = (component.component_percentage / 100) * totalCost
    component.component_cost = componentCost
    const totalSubPercentage = component.sub_components.reduce(
      (sum, sub) => sum + sub.subcomponent_percentage,
      0,
    )
    component.sub_components.forEach((sub) => {
      sub.subcomponent_cost = (sub.subcomponent_percentage / totalSubPercentage) * componentCost
    })
  })
  return data
}

// Function to transform data for Compare Tab
export function transformSimulationComparisonData(data) {
  const result = []

  for (const item of data) {
    const { id, type, name, country_of_origin, total_cost, description, components = [] } = item

    const transformedItem = {
      id,
      type,
      name,
      country_of_origin,
      total_cost,
      description,
      chartData: [],
    }

    for (const comp of components) {
      const {
        component_name,
        component_order_id,
        section_id,
        component_cost,
        sub_components = [],
        component_details,
      } = comp

      const transformedComponent = {
        component_name,
        component_order_id: Number(component_order_id),
        section_id,
        total_cost: component_cost,
        subcomponents: [],
        component_details,
      }

      for (const sub of sub_components) {
        const {
          subcomponent_name,
          subcomponent_cost,
          subcomponent_order_id,
          subcomponent_index_ticker,
          is_design_change,
          subcomponent_index_ticker_country,
          subcomponent_index_ticker_description,
          subcomponent_index_ticker_price,
        } = sub

        transformedComponent.subcomponents.push({
          subcomponent_name,
          index_ticker: subcomponent_index_ticker ?? null,
          subcomponent_cost,
          total_cost: subcomponent_cost,
          is_design_change,
          subcomponent_order_id: Number(subcomponent_order_id),
          subcomponent_index_ticker_country,
          subcomponent_index_ticker_description,
          subcomponent_index_ticker_price,
        })
      }

      // sort subcomponents by order ID
      transformedComponent.subcomponents.sort(
        (a, b) => a.subcomponent_order_id - b.subcomponent_order_id,
      )

      transformedItem.chartData.push(transformedComponent)
    }

    // sort components by component_order_id
    transformedItem.chartData.sort((a, b) => a.component_order_id - b.component_order_id)

    result.push(transformedItem)
  }

  return result
}

export const orderTrendData = (data) => {
  const order = ['PO', 'Base Should Cost', 'Simulation']
  const orderedData = data.sort((a, b) => {
    // Get the index of each type in the order array
    const indexA = order.indexOf(a.type)
    const indexB = order.indexOf(b.type)

    // Compare the indices to determine the sort order
    return indexA - indexB
  })
  return orderedData
}

export const parsePercentage = (value) => {
  if (typeof value === 'string') {
    return Number(value.replace('%', '')) || 0
  }
  return Number(value) || 0
}

// transfrom breakdown of breakdown generated by gen ai (old flow)
export const TransformComponentDataV2 = (data) => {
  if (!Array.isArray(data)) return []

  const mergedMap = new Map()
  const componentLabelMap = {
    'raw material cost': 'Raw Material',
    'processing cost': 'Conversion',
    'labor cost': 'Labor',
    'packaging cost': 'Packaging',
    'overhead cost': 'Overhead & Margin',
    'profit margin': 'Overhead & Margin',
    'freight cost': 'Logistics',
  }

  const validComponents = new Set(Object.keys(componentLabelMap))

  data.forEach((component) => {
    const name = component.component_name?.toLowerCase()

    if (!validComponents.has(name)) return

    const label = componentLabelMap[name]
    const percentage = parsePercentage(component.component_percentage)

    const subComponents = component.sub_components || component.sub_component || []

    let detailData = []

    // add freight cost subcomponent in logistics
    if (name === 'freight cost') {
      detailData = [
        {
          label: 'Freight Cost',
          percentage: 100,
          indexTicker: '',
          indexTickerDescription: '',
          indexId: uuidv4(),
        },
      ]
    } else {
      // normal subcomponent processing
      detailData = subComponents
        .filter(
          (sub) => (sub.subcomponent_name || sub.subcomponet_name)?.toLowerCase() !== 'product',
        )
        .map((sub) => ({
          label: capitalizeFirstLetter(sub.subcomponent_name),
          percentage: parsePercentage(sub.subcomponent_percentage),
          indexTicker:
            sub.subcomponent_index_ticker === 'NULL' || sub.subcomponent_index_ticker === 'null'
              ? ''
              : sub.subcomponent_index_ticker || '',
          indexTickerDescription: sub?.index_ticker_description || '',
          indexId: uuidv4(),
        }))
    }

    if (mergedMap.has(label)) {
      const existing = mergedMap.get(label)
      existing.percentage += percentage
      existing.data.push(...detailData)
    } else {
      mergedMap.set(label, {
        label,
        percentage,
        data: detailData,
      })
    }
  })

  // sort based on cost_component_order
  return Array.from(mergedMap.values()).sort(
    (a, b) => COST_COMPONENT_ORDER[a.label] - COST_COMPONENT_ORDER[b.label],
  )
}

// new top down flow functions
const COMPONENT_NAME_MAP = {
  'raw material': 'Raw Material',
  conversion: 'Conversion',
  labor: 'Labor',
  packaging: 'Packaging',
  'overhead & margin': 'Overhead & Margin',
}

// format the genAI breakdown in new Top Down Flow
export const transformGenAIBreakdownHelper = (apiResponse) => {
  if (!apiResponse?.data || !Array.isArray(apiResponse.data)) {
    return []
  }

  return apiResponse.data.map((component) => {
    const { component_name, component_percentage, sub_components, sub_component } = component

    const normalizedSubComponents = sub_components || sub_component || []

    return {
      label: COMPONENT_NAME_MAP[component_name] || capitalizeFirstLetter(component_name),
      percentage: parsePercentage(component_percentage) || 0,
      data: normalizedSubComponents.map((sub) => {
        return {
          label: capitalizeFirstLetter(sub.subcomponent_name),
          percentage: parsePercentage(sub.subcomponent_percentage) || 0,
          indexTicker:
            sub.subcomponent_index_ticker === 'NULL' || sub.subcomponent_index_ticker === 'null'
              ? ''
              : sub.subcomponent_index_ticker || '',
          indexId: uuidv4(),
          indexTickerDescription: sub?.index_ticker_description || '',
        }
      }),
    }
  })
}